import { Reducer } from "redux";

import {
	BillingInfo,
	ChargebeePortalSession,
	ChargebeeInvoice,
	ChargebeeInvoiceResponse,
	CompanyUserCount,
	ConfigurationSettingsResponse,
	LibraryItem,
	PricingPlanEstimateData,
	PricingPlanSummary,
	DatePattern,
	ChargebeeCreditNoteResponse,
	ChargebeeCreditNote,
	PricingPlan,
	PricingSummary,
	UserData,
	TaxCategory,
	TaxRegion,
	UserCompany,
	UserCalendar,
	UserEmailSignature
} from "@/Contracts/Users";

import { UsersActionTypes, UsersActions } from "@/Actions/User";
import { AmazonProductAppliedFilters, cache, Integrations } from "@/Contracts";

export interface UsersState {
	readonly isLoading: boolean;
	readonly userData: UserData;
	readonly taxCategoriesList: TaxCategory[];
	readonly taxRegionsList: TaxRegion[];
	readonly configurationSettings: ConfigurationSettingsResponse;
	readonly chargebeePortalSession: ChargebeePortalSession | null;
	readonly billingInfo: BillingInfo;
	readonly updateCardModalVisible: boolean;
	readonly isBillingInfoLoading: boolean;
	readonly userCompany: UserCompany;
	readonly companyUserList: UserData[];
	readonly companyUserCount: CompanyUserCount;
	readonly libraryList: LibraryItem[];
	readonly isLibraryLoading: boolean;
	readonly userCalendars: UserCalendar[] | null;
	readonly chargebeeInvoicesList: ChargebeeInvoiceResponse;
	readonly chargebeeCreditNotesList: ChargebeeCreditNoteResponse;
	readonly addTaxRegionDrawerOpen: boolean;
	readonly editTaxRegionDrawerOpen: boolean;
	readonly addTaxCategoryDrawerOpen: boolean;
	readonly editTaxCategoryDrawerOpen: boolean;
	readonly emailVerification: null;
	readonly emailSignatures: UserEmailSignature;
	readonly datePatterns: DatePattern[] | null;
	readonly subscriptions: PricingPlan[];
	readonly subscriptionDetails: PricingPlanSummary;
	readonly subscriptionEstimate: PricingPlanEstimateData;
	readonly isSubscriptionDetailsLoading: boolean;
	readonly amazonAppliedFilters: AmazonProductAppliedFilters | null;
}

export const initialUserState: UsersState = {
	isLoading: false,
	userData: new UserData(),
	taxCategoriesList: new Array<TaxCategory>(),
	taxRegionsList: new Array<TaxRegion>(),
	configurationSettings: new ConfigurationSettingsResponse(),
	userCompany: new UserCompany(),
	companyUserList: new Array<UserData>(),
	companyUserCount: new CompanyUserCount(),
	libraryList: [],
	isLibraryLoading: false,
	isSubscriptionDetailsLoading: false,
	chargebeePortalSession: null,
	billingInfo: new BillingInfo(),
	updateCardModalVisible: false,
	isBillingInfoLoading: false,
	userCalendars: null,
	chargebeeInvoicesList: {
		data: new Array<ChargebeeInvoice>(),
		filtered_count_map: { all: 0 }
	},
	chargebeeCreditNotesList: { data: new Array<ChargebeeCreditNote>() },
	addTaxRegionDrawerOpen: false,
	editTaxRegionDrawerOpen: false,
	addTaxCategoryDrawerOpen: false,
	editTaxCategoryDrawerOpen: false,
	emailVerification: null,
	emailSignatures: new UserEmailSignature(),
	datePatterns: null,
	subscriptions: new Array<PricingPlan>(),
	subscriptionDetails: new PricingSummary(),
	subscriptionEstimate: new PricingPlanEstimateData(),
	amazonAppliedFilters: null
};

const getInitialUsersState = (): UsersState => {
	const data = {
		...initialUserState,
		userData: cache.data.userData || initialUserState.userData,
		configurationSettings:
			cache.data.configurationSettings ||
			initialUserState.configurationSettings,
		companyUserList:
			cache.data.companyUserList || initialUserState.companyUserList,
		userCompany: cache.data.userCompany || initialUserState.userCompany
	};
	return data;
};

export const UserReducer: Reducer<UsersState, UsersActions> = (
	state: UsersState = getInitialUsersState(),
	action: UsersActions
): UsersState => {
	switch (action.type) {
		case UsersActionTypes.SHOW_USER_LOADER: {
			return {
				...state,
				isLoading: true
			};
		}
		case UsersActionTypes.HIDE_USER_LOADER: {
			return {
				...state,
				isLoading: false
			};
		}
		case UsersActionTypes.USER_DATA_SUCCESSFUL: {
			return {
				...state,
				userData: action.userData
			};
		}
		case UsersActionTypes.USER_DATA_UPDATED: {
			return {
				...state,
				userData: action.userData
			};
		}
		case UsersActionTypes.EDIT_USER_DATA_SUCCESSFUL: {
			return {
				...state,
				userData: action.userData
			};
		}
		case UsersActionTypes.SET_TWO_FACTOR_AUTH_SUCCESSFUL: {
			return {
				...state,
				userCompany: action.userCompany
			};
		}
		case UsersActionTypes.SET_SSO_MANDATORY_SUCCESSFUL: {
			return {
				...state,
				userCompany: action.userCompany
			};
		}
		case UsersActionTypes.SET_SSO_ENABLE_SUCCESSFUL: {
			return {
				...state,
				userCompany: action.userCompany
			};
		}
		case UsersActionTypes.CONFIGURATION_SETTINGS_SUCCESSFUL: {
			return {
				...state,
				configurationSettings: action.configurationSettings
			};
		}
		case UsersActionTypes.USERREQUEST_JOINCOMPANY_SUCCESSFUL: {
			return {
				...state,
				configurationSettings: action.configurationSettings
			};
		}
		case UsersActionTypes.USERREQUEST_WITHDRAWCOMPANY_SUCCESSFUL: {
			return {
				...state,
				configurationSettings: action.configurationSettings
			};
		}
		case UsersActionTypes.USER_COMPANY_CONFIG_UPDATE_SUCCESSFUL: {
			return {
				...state,
				configurationSettings: action.configurationSettings
			};
		}
		case UsersActionTypes.COMPANY_DATA_UPDATED: {
			return {
				...state,
				userCompany: action.userCompany
			};
		}
		case UsersActionTypes.USER_COMPANY_SUCCESSFUL: {
			const isCustomDomain =
				action.userCompany.domain
					.replace("https://", "")
					.split(".")[1] !== process.env.REACT_APP_DEFAULT_DOMAIN;

			const splittedDomain = action.userCompany.domain
				.replace("https://", "")
				.split(".");
			return {
				...state,
				userCompany: {
					...action.userCompany,
					sub_domain: isCustomDomain
						? action.userCompany.domain
						: splittedDomain?.[0]
				}
			};
		}
		case UsersActionTypes.GET_DATE_PATTERNS_SUCCESSFUL: {
			return {
				...state,
				datePatterns: action.datePatterns
			};
		}
		case UsersActionTypes.UDPATE_TRANSLATE_LANGUAGE_SUCCESSFUL:
		case UsersActionTypes.EDIT_USER_COMPANY_SUCCESSFUL: {
			return {
				...state,
				userCompany: action.editUserCompany
			};
		}
		case UsersActionTypes.TAX_CATEGORIES_LIST_ACTION_SUCCESSFUL: {
			return {
				...state,
				taxCategoriesList: action.taxCategoriesList
			};
		}
		case UsersActionTypes.TAX_REGIONS_LIST_ACTION_SUCCESSFUL: {
			return {
				...state,
				taxRegionsList: action.taxRegionsList
			};
		}
		case UsersActionTypes.ADD_TAX_REGION_ACTION_SUCCESSFUL: {
			return {
				...state,
				taxRegionsList: [action.addTaxRegion].concat(
					state.taxRegionsList
				)
			};
		}
		case UsersActionTypes.EDIT_TAX_REGION_ACTION_SUCCESSFUL: {
			const taxRegionsList = state.taxRegionsList.filter(
				taxRegion => taxRegion.id !== action.editTaxRegion.id
			);
			const updateTaxRegionIndex = state.taxRegionsList.findIndex(
				taxRegion => taxRegion.id === action.editTaxRegion.id
			);
			if (updateTaxRegionIndex > -1) {
				taxRegionsList.splice(
					updateTaxRegionIndex,
					0,
					action.editTaxRegion
				);
			}
			return {
				...state,
				taxRegionsList
			};
		}
		case UsersActionTypes.ADD_TAX_CATEGORY_ACTION_SUCCESSFUL: {
			return {
				...state,
				taxCategoriesList: [action.addTaxCategory].concat(
					state.taxCategoriesList
				)
			};
		}
		case UsersActionTypes.EDIT_TAX_CATEGORY_ACTION_SUCCESSFUL: {
			const taxCategoriesList = state.taxCategoriesList.filter(
				taxCategory => taxCategory.id !== action.editTaxCategory.id
			);
			const updateTaxCategoryIndex = state.taxCategoriesList.findIndex(
				taxCategory => taxCategory.id === action.editTaxCategory.id
			);
			if (updateTaxCategoryIndex > -1) {
				taxCategoriesList.splice(
					updateTaxCategoryIndex,
					0,
					action.editTaxCategory
				);
			}
			return {
				...state,
				taxCategoriesList
			};
		}
		case UsersActionTypes.ADD_TAX_REGION_DRAWER_OPENED: {
			return {
				...state,
				addTaxRegionDrawerOpen: true
			};
		}
		case UsersActionTypes.ADD_TAX_REGION_DRAWER_CLOSED: {
			return {
				...state,
				addTaxRegionDrawerOpen: false
			};
		}
		case UsersActionTypes.EDIT_TAX_REGION_DRAWER_OPENED: {
			return {
				...state,
				editTaxRegionDrawerOpen: true
			};
		}
		case UsersActionTypes.EDIT_TAX_REGION_DRAWER_CLOSED: {
			return {
				...state,
				editTaxRegionDrawerOpen: false
			};
		}
		case UsersActionTypes.ADD_TAX_CATEGORY_DRAWER_OPENED: {
			return {
				...state,
				addTaxCategoryDrawerOpen: true
			};
		}
		case UsersActionTypes.ADD_TAX_CATEGORY_DRAWER_CLOSED: {
			return {
				...state,
				addTaxCategoryDrawerOpen: false
			};
		}
		case UsersActionTypes.EDIT_TAX_CATEGORY_DRAWER_OPENED: {
			return {
				...state,
				editTaxCategoryDrawerOpen: true
			};
		}
		case UsersActionTypes.EDIT_TAX_CATEGORY_DRAWER_CLOSED: {
			return {
				...state,
				editTaxCategoryDrawerOpen: false
			};
		}
		case UsersActionTypes.CHANGE_COMPANY_LOGO_SUCCESSFUL: {
			return {
				...state,
				userCompany: action.changeLogoUserCompany
			};
		}
		case UsersActionTypes.COMPANY_USERS_SUCCESSFUL: {
			return {
				...state,
				companyUserList: action.companyUserList
					.sort((a, _b) => (a.bot_user_source ? -1 : 1))
					.sort((a, _b) => (a.is_disabled ? 1 : -1)),
				companyUserCount: action.companyUserCount
			};
		}
		case UsersActionTypes.DELETE_COMPANY_USER_SUCCESSFUL: {
			return {
				...state,
				companyUserList: state.companyUserList.filter(
					companyUser =>
						companyUser.id !== action.deleteCompanyUser.id
				)
			};
		}
		case UsersActionTypes.ADD_COMPANY_USER_SUCCESSFUL: {
			return {
				...state,
				companyUserList: [action.addCompanyUser].concat(
					state.companyUserList
				)
			};
		}
		case UsersActionTypes.EDIT_COMPANY_USER_SUCCESSFUL: {
			const companyUserList = state.companyUserList.filter(
				companyUser => companyUser.id !== action.editCompanyUser.id
			);
			const updateCompanyUserIndex = state.companyUserList.findIndex(
				companyUser => companyUser.id === action.editCompanyUser.id
			);
			if (updateCompanyUserIndex > -1) {
				companyUserList.splice(
					updateCompanyUserIndex,
					0,
					action.editCompanyUser
				);
			}
			return {
				...state,
				companyUserList
			};
		}
		case UsersActionTypes.EDIT_CURRENT_COMPANY_USER_SUCCESSFUL: {
			const companyUserList = state.companyUserList.filter(
				companyUser => companyUser.id !== action.editCompanyUser.id
			);
			const updateCompanyUserIndex = state.companyUserList.findIndex(
				companyUser => companyUser.id === action.editCompanyUser.id
			);
			if (updateCompanyUserIndex > -1) {
				companyUserList.splice(
					updateCompanyUserIndex,
					0,
					action.editCompanyUser
				);
			}
			return {
				...state,
				companyUserList,
				userData: action.editCompanyUser
			};
		}
		case UsersActionTypes.ADD_COMPANY_EMAIL_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_COMPANY_CONNECTWISE_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.UPDATE_INITIAL_SYNC_STATUS:
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: {
						...state.userCompany.integrations,
						[action.integrationToUpdate]: {
							...state.userCompany.integrations[
								action.integrationToUpdate as keyof Integrations
							],
							is_initial_sync_pending: false
						}
					}
				}
			};
		case UsersActionTypes.DELETE_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_COMPANY_AUTOTASK_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_COMPANY_KASEYA_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_COMPANY_ZAPIER_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.SHOW_LIBRARY_LOADER: {
			return { ...state, isLibraryLoading: true };
		}
		case UsersActionTypes.HIDE_LIBRARY_LOADER: {
			return { ...state, isLibraryLoading: false };
		}
		case UsersActionTypes.SHOW_SUBSCRIPTION_DETAILS_LOADER: {
			return { ...state, isSubscriptionDetailsLoading: true };
		}
		case UsersActionTypes.HIDE_SUBSCRIPTION_DETAILS_LOADER: {
			return { ...state, isSubscriptionDetailsLoading: false };
		}
		case UsersActionTypes.FETCH_LIBRARY_LIST_SUCCESSFUL: {
			return { ...state, libraryList: action.libraryList };
		}
		case UsersActionTypes.CHARGEBEE_PORTAL_SESSION_SUCCESSFUL: {
			return { ...state, chargebeePortalSession: action.portalSession };
		}
		case UsersActionTypes.BILLING_INFO_SUCCESSFUL: {
			return { ...state, billingInfo: action.billingInfo };
		}
		case UsersActionTypes.SUBSCRIPTION_PLANS_SUCCESSFUL: {
			return { ...state, subscriptions: action.subscriptions };
		}
		case UsersActionTypes.SUBSCRIPTION_DETAILS_SUCCESSFUL: {
			return {
				...state,
				subscriptionDetails: action.subscriptionDetails
			};
		}
		case UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_RESET: {
			return {
				...state,
				subscriptionEstimate: new PricingPlanEstimateData()
			};
		}
		case UsersActionTypes.SUBSCRIPTION_PLAN_ADD_USER_SUCCESSFUL: {
			return {
				...state,
				subscriptionDetails: action.subscriptionDetails
			};
		}
		case UsersActionTypes.SUBSCRIPTION_PLAN_REMOVE_USER_SUCCESSFUL: {
			return {
				...state,
				subscriptionDetails: action.subscriptionDetails
			};
		}
		case UsersActionTypes.SUBSCRIPTION_PLAN_UPGRADE_DOWNGRADE_SUCCESSFUL: {
			return {
				...state,
				subscriptionDetails: action.subscriptionDetails
			};
		}
		case UsersActionTypes.SUBSCRIPTION_PLAN_ESTIMATE_SUCCESSFUL: {
			return {
				...state,
				subscriptionEstimate: action.subscriptionEstimate
			};
		}
		case UsersActionTypes.CONFIRM_CARD_ADDED_SUCCESSFUL: {
			return { ...state, billingInfo: action.billingInfo };
		}
		case UsersActionTypes.SHOW_UPDATE_CARD_MODAL: {
			return {
				...state,
				updateCardModalVisible: true
			};
		}
		case UsersActionTypes.HIDE_UPDATE_CARD_MODAL: {
			return {
				...state,
				updateCardModalVisible: false
			};
		}
		case UsersActionTypes.SHOW_BILLING_INFO_LOADER: {
			return {
				...state,
				isBillingInfoLoading: true
			};
		}
		case UsersActionTypes.HIDE_BILLING_INFO_LOADER: {
			return {
				...state,
				isBillingInfoLoading: false
			};
		}
		case UsersActionTypes.ADD_EMAIL_ALIAS_SUCCESSFUL: {
			return {
				...state,
				configurationSettings: {
					...state.configurationSettings,
					company_configs: {
						...state.configurationSettings.company_configs,
						integrations: action.addEmailAliasResponse.integrations
					}
				}
			};
		}
		case UsersActionTypes.DELETE_EMAIL_ALIAS_SUCCESSFUL: {
			return {
				...state,
				configurationSettings: {
					...state.configurationSettings,
					company_configs: {
						...state.configurationSettings.company_configs,
						integrations:
							action.deleteEmailAliasResponse.integrations
					}
				}
			};
		}
		case UsersActionTypes.ADD_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_COMPANY_INGRAM_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_COMPANY_TECH_DATA_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_D_AND_H_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_D_AND_H_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_SYNNEX_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_COMPANY_SYNCRO_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_INFUSION_SOFT_INTEGRATION_SUCCESFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_INFUSION_SOFT_INTEGRATION_SUCCESFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_SYNNEX_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.EDIT_QUOTE_DEFAULT_LABEL_COMPANY_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					settings: {
						...state.userCompany.settings,
						pricing_table_default_label: action.quoteLabels
					}
				}
			};
		}
		case UsersActionTypes.DISABLE_USER_SUCCESSFUL: {
			return {
				...state,
				companyUserList: state.companyUserList.map(users => {
					if (users.id === action.userData.id) {
						return action.userData;
					}
					return users;
				})
			};
		}
		case UsersActionTypes.ENABLE_USER_SUCCESSFUL: {
			return {
				...state,
				companyUserList: state.companyUserList.map(users => {
					if (users.id === action.userData.id) {
						return action.userData;
					}
					return users;
				})
			};
		}
		case UsersActionTypes.FETCH_USER_CALENDARS_SUCCESSFUL: {
			return {
				...state,
				userCalendars: action.userCalendars
			};
		}
		case UsersActionTypes.RESET_USER_CALENDARS: {
			return {
				...state,
				userCalendars: null
			};
		}
		case UsersActionTypes.FETCH_COMPANY_INTEGRATIONS_SUCCESSFUL: {
			return {
				...state,
				configurationSettings: {
					...state.configurationSettings,
					company_configs: {
						integrations: action.integrations
					}
				}
			};
		}
		case UsersActionTypes.CHARGEBEE_INVOICES_LIST_SUCCESSFUL: {
			return {
				...state,
				chargebeeInvoicesList: {
					...state.chargebeeInvoicesList,
					...action.chargebeeInvoicesList,
					data: state.chargebeeInvoicesList.data.concat(
						action.chargebeeInvoicesList.data
					)
				}
			};
		}
		case UsersActionTypes.CHARGEBEE_CREDIT_NOTES_LIST_SUCCESSFUL: {
			return {
				...state,
				chargebeeCreditNotesList: {
					...state.chargebeeCreditNotesList,
					...action.chargebeeCreditNotesList,
					data: state.chargebeeCreditNotesList.data.concat(
						action.chargebeeCreditNotesList.data
					)
				}
			};
		}
		case UsersActionTypes.FETCH_EMAIL_SIGNATURES_SUCCESSFUL: {
			return {
				...state,
				emailSignatures: action.emailSignature
			};
		}
		case UsersActionTypes.UPDATE_EMAIL_SIGNATURES_SUCCESSFUL: {
			return {
				...state,
				emailSignatures: action.emailSignature
			};
		}
		case UsersActionTypes.ONBOARDING_CALL_SCHEDULED_SUCCESSFUL: {
			return {
				...state,
				billingInfo: {
					...state.billingInfo,
					billing_status: action.billingStatus
				}
			};
		}
		case UsersActionTypes.RESET_USER_STATE: {
			return initialUserState;
		}
		case UsersActionTypes.ADD_DICKER_DATA_INTERGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_DICKER_DATA_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.ADD_WESTCOAST_INTERGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_WESTCOAST_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.CONNECT_DATEK_DISTRIBUTOR_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_CUSTOM_DISTRIBUTOR_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}
		case UsersActionTypes.DELETE_PAYMENTS_CONNECTION_SUCCESSFUL:
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.company.integrations
				}
			};
		case UsersActionTypes.DELETE_STRIPE_CONNECTION_SUCCESSFUL:
			return {
				...state,
				userCompany: { ...action.company }
			};
		case UsersActionTypes.DELETE_HALO_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: { ...action.integrations }
				}
			};
		}
		case UsersActionTypes.ADD_HALO_INTEGRATION_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: { ...action.newIntegrations }
				}
			};
		}
		case UsersActionTypes.ADD_CUSTOM_DISTRIBUTOR_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: { ...action.integrations }
				}
			};
		}
		case UsersActionTypes.EDIT_COMPANY_PREFERENCES_SUCCESSFUL: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					settings: {
						...state.userCompany.settings,
						preference_settings:
							action.userCompany.settings.preference_settings
					}
				}
			};
		}

		case UsersActionTypes.UPDATE_INTEGRATIION_PAGE_DETAILS: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					integrations: action.integrations
				}
			};
		}

		case UsersActionTypes.AI_WRITER_TRIAL_INFO_UPDATED: {
			return {
				...state,
				userCompany: {
					...state.userCompany,
					settings: {
						...state.userCompany.settings,
						trial_info_for_ai_writer: action.aiWriterTrialInfo
					}
				}
			};
		}

		case UsersActionTypes.UPDATE_AMAZON_APPLIED_FILTER:
			return {
				...state,
				amazonAppliedFilters: action.amazonAppliedFilters
			};

		default: {
			return state;
		}
	}
};

export default UserReducer;
